import { useCallback, useEffect, useState, type ComponentProps, type ReactNode } from "react";
import { usePanelRef, useDefaultLayout, type LayoutStorage } from "react-resizable-panels";
import {
  BookUser,
  Inbox,
  LayoutGrid,
  ListTree,
  MessageSquare,
  Images,
  Ellipsis,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  PinOff,
  Plus,
  X,
} from "lucide-react";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/client/components/ui/resizable";
import { Sheet, SheetClose, SheetContent, SheetTitle } from "@/client/components/ui/sheet";
import { ToggleGroup, ToggleGroupItem } from "@/client/components/ui/toggle-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/client/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/client/components/ui/tooltip";
import { CodexPanel } from "@/client/components/codex/codex-panel";
import { FragmentDndProvider } from "@/client/components/fragments/fragment-dnd";
import { InboxPanel } from "@/client/components/fragments/inbox-panel";
import { PlacementReviewDialog } from "@/client/components/fragments/placement-review-dialog";
import { QuickCaptureDialog, useQuickCaptureShortcut } from "@/client/components/fragments/quick-capture-dialog";
import { InspectorPanel } from "@/client/components/inspector/inspector-panel";
import { Button } from "@/client/components/ui/button";
import {
  setBoardDetailsOpen,
  setBoardDetailsPinned,
  useBoardDetailsOpen,
  useBoardDetailsPinned,
} from "@/client/hooks/use-board-state";
import { onDraftRequest } from "@/client/hooks/use-story-ai";
import { useCheckRunner } from "@/client/hooks/use-checks";
import { useIsMobile } from "@/client/hooks/use-mobile";
import { StructureTree } from "@/client/components/structure/structure-tree";
import { TabbedContent } from "@/client/components/tabs/tabbed-content";
import { StoryDataProvider, useStoryData } from "@/client/hooks/use-story-data";
import { useTabs } from "@/client/hooks/use-tabs-context";
import { clearUndo, redo, undo } from "@/client/lib/undo";
import { MOD } from "@/client/lib/keys";
import { cn } from "@/client/lib/utils";
import { ChatsPanel } from "./chats-panel";
import { ArtPanel, artAttentionCount } from "@/client/components/art/art-panel";
import { useStoryJobs } from "@/client/hooks/use-art";

type Section = "structure" | "inbox" | "codex" | "chats" | "art";

const SECTIONS: { id: Section; label: string; icon: typeof ListTree }[] = [
  { id: "structure", label: "Structure", icon: ListTree },
  { id: "inbox", label: "Ideas", icon: Inbox },
  { id: "codex", label: "Codex", icon: BookUser },
  { id: "chats", label: "Chats", icon: MessageSquare },
  { id: "art", label: "Art", icon: Images },
];

const SECTION_KEY = "renteller-navigator-section";
/** Which side panels were open before the board folded them away */
const BEFORE_BOARD_KEY = "renteller-panels-before-board";

// Panel sizes aren't story content, so they're kept per device under one
// flat key rather than the library's colon-separated one, which sign-out
// cleanup would treat as account data.
const layoutStorage: LayoutStorage = {
  getItem: () => localStorage.getItem("renteller-workspace-layout"),
  setItem: (_key, value) => localStorage.setItem("renteller-workspace-layout", value),
};

function storedSection(): Section {
  const stored = typeof localStorage !== "undefined" ? localStorage.getItem(SECTION_KEY) : null;
  return stored === "inbox" || stored === "codex" || stored === "chats" || stored === "art" ? stored : "structure";
}

/** A rail button: its icon over a short name, with the longer name and shortcut on hover */
function RailButton({
  label,
  name,
  shortcut,
  active,
  onClick,
  badge,
  children,
}: {
  /** What it does, for the tooltip and screen readers */
  label: string;
  /** The short name under the icon, when it differs from the label */
  name?: string;
  shortcut?: string;
  active?: boolean;
  onClick: () => void;
  badge?: number;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          aria-pressed={active}
          className={cn(
            "relative flex w-14 flex-col items-center justify-center gap-0.5 rounded-md py-1.5 text-[11px] leading-tight text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
            active && "bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary"
          )}
        >
          {children}
          <span className="max-w-full truncate px-0.5">{name ?? label}</span>
          {badge ? (
            <span className="absolute right-1 top-0.5 min-w-4 rounded-full bg-primary px-1 text-center text-[10px] font-semibold leading-4 text-primary-foreground">
              {badge > 99 ? "99+" : badge}
            </span>
          ) : null}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">
        {label}
        {shortcut && <span className="ml-2 text-muted-foreground">{shortcut}</span>}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The story screen: a rail of navigator sections on the left, the open tabs
 * in the middle, and an inspector for whatever is open on the right. Both
 * side panels resize and collapse, from the rail or with Ctrl+\ and
 * Ctrl+Shift+\; the layout is remembered.
 *
 * On a phone the open tab fills the screen, a bottom bar replaces the rail,
 * and the navigator and inspector slide in as sheets over it.
 */
export function StoryWorkspace({ storyId }: { storyId: string }) {
  return (
    <StoryDataProvider storyId={storyId}>
      <Workspace storyId={storyId} />
    </StoryDataProvider>
  );
}

function Workspace({ storyId }: { storyId: string }) {
  const [section, setSectionState] = useState<Section>(storedSection);
  const [navigatorOpen, setNavigatorOpen] = useState(true);
  const [inspectorOpen, setInspectorOpen] = useState(true);
  const [captureOpen, setCaptureOpen] = useState(false);
  const navigatorRef = usePanelRef();
  const inspectorRef = usePanelRef();
  const { unplaced } = useStoryData().fragments;
  const artAttention = artAttentionCount(useStoryJobs(storyId));
  const { activeTab, openBoardTab } = useTabs();
  const layout = useDefaultLayout({ id: "workspace", storage: layoutStorage });
  const mobile = useIsMobile();
  const [sheet, setSheet] = useState<"navigator" | "inspector" | null>(null);

  const openCapture = useCallback(() => setCaptureOpen(true), []);
  useQuickCaptureShortcut(openCapture);

  const toggleNavigator = useCallback(() => {
    if (mobile) return setSheet((open) => (open === "navigator" ? null : "navigator"));
    const panel = navigatorRef.current;
    if (panel?.isCollapsed()) panel.expand();
    else panel?.collapse();
  }, [mobile, navigatorRef]);
  const toggleInspector = useCallback(() => {
    if (mobile) return setSheet((open) => (open === "inspector" ? null : "inspector"));
    const panel = inspectorRef.current;
    if (panel?.isCollapsed()) panel.expand();
    else panel?.collapse();
  }, [mobile, inspectorRef]);
  const showInspector = useCallback(() => {
    if (mobile) setSheet("inspector");
    else inspectorRef.current?.expand();
  }, [mobile, inspectorRef]);

  // On a phone, opening something from a sheet puts it on screen
  const activeTabId = activeTab?.id;
  useEffect(() => setSheet(null), [activeTabId]);

  // The board gets the whole width: both side panels fold away while it's
  // open, the details docked only when pinned, and come back on leaving it.
  // What they were is kept on the device, so a reload on the board still
  // brings them back.
  const onBoard = activeTab?.type === "board";
  const detailsPinned = useBoardDetailsPinned();

  // On a phone the board has no drawer: a tapped card's details come up in the sheet
  const boardDetailsOpen = useBoardDetailsOpen();
  useEffect(() => {
    if (mobile && onBoard && boardDetailsOpen) setSheet("inspector");
  }, [mobile, onBoard, boardDetailsOpen]);
  const changeSheet = (next: typeof sheet) => {
    setSheet(next);
    // Closed, so tapping a card again brings them back
    if (next !== "inspector") setBoardDetailsOpen(false);
  };
  useEffect(() => {
    const navigator = navigatorRef.current;
    const inspector = inspectorRef.current;
    if (mobile || !navigator || !inspector) return;
    if (onBoard) {
      if (!localStorage.getItem(BEFORE_BOARD_KEY)) {
        localStorage.setItem(
          BEFORE_BOARD_KEY,
          JSON.stringify({ navigator: !navigator.isCollapsed(), inspector: !inspector.isCollapsed() })
        );
      }
      navigator.collapse();
      return;
    }
    const before = localStorage.getItem(BEFORE_BOARD_KEY);
    if (!before) return;
    localStorage.removeItem(BEFORE_BOARD_KEY);
    const was = JSON.parse(before) as { navigator: boolean; inspector: boolean };
    if (was.navigator) navigator.expand();
    if (was.inspector) inspector.expand();
  }, [onBoard, mobile, navigatorRef, inspectorRef]);
  useEffect(() => {
    const inspector = inspectorRef.current;
    if (mobile || !onBoard || !inspector) return;
    if (detailsPinned) inspector.expand();
    else inspector.collapse();
  }, [onBoard, detailsPinned, mobile, inspectorRef]);

  // Out-of-date marks stay current while the story is open
  useCheckRunner(storyId);

  // Drafting from the board happens in the inspector; bring it out if it's hidden
  useEffect(() => onDraftRequest(showInspector), [showInspector]);

  // Each story has its own undo history
  useEffect(() => {
    clearUndo();
    return clearUndo;
  }, [storyId]);

  // Ctrl+Z and Ctrl+Shift+Z (or Ctrl+Y) undo and redo planning changes. In a
  // text field they're left to the browser, which undoes typing.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName ?? "")) return;
      if (document.querySelector("[role='dialog'], [role='alertdialog']")) return;
      if (event.code === "KeyZ") {
        event.preventDefault();
        void (event.shiftKey ? redo() : undo());
      } else if (event.code === "KeyY" && !event.shiftKey) {
        event.preventDefault();
        void redo();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== "Backslash" || !(event.ctrlKey || event.metaKey) || event.altKey) return;
      event.preventDefault();
      if (event.shiftKey) toggleInspector();
      else toggleNavigator();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggleNavigator, toggleInspector]);

  const showSection = (next: Section) => {
    if (mobile) {
      setSheet(next === section && sheet === "navigator" ? null : "navigator");
    } else if (next === section && navigatorOpen) {
      navigatorRef.current?.collapse();
      return;
    } else if (!navigatorOpen) {
      navigatorRef.current?.expand();
    }
    setSectionState(next);
    localStorage.setItem(SECTION_KEY, next);
  };

  const showInbox = () => {
    setSectionState("inbox");
    localStorage.setItem(SECTION_KEY, "inbox");
    if (mobile) setSheet("navigator");
    else navigatorRef.current?.expand();
  };

  const sectionPanel = (
    <>
      {section === "structure" && <StructureTree storyId={storyId} />}
      {section === "inbox" && <InboxPanel storyId={storyId} />}
      {section === "codex" && (
        <div className="h-full pl-2">
          <CodexPanel storyId={storyId} activeEntryId={activeTab?.type === "codex" ? activeTab.entityId : null} />
        </div>
      )}
      {section === "chats" && <ChatsPanel storyId={storyId} />}
      {section === "art" && <ArtPanel storyId={storyId} />}
    </>
  );

  const dialogs = (
    <>
      <QuickCaptureDialog storyId={storyId} open={captureOpen} onOpenChange={setCaptureOpen} />
      <PlacementReviewDialog storyId={storyId} />
    </>
  );

  if (mobile) {
    return (
      <FragmentDndProvider storyId={storyId}>
        <div data-region="workspace" className="flex min-h-0 flex-1 flex-col">
          <div data-region="panel" data-panel="center" className="min-h-0 flex-1 overflow-hidden">
            <TabbedContent storyId={storyId} onShowInbox={showInbox} onCapture={openCapture} />
          </div>
          <nav
            data-region="bottom-bar"
            aria-label="Story sections"
            className="grid shrink-0 grid-cols-6 border-t bg-panel pb-[env(safe-area-inset-bottom)]"
          >
            {SECTIONS.filter((s) => s.id !== "chats" && s.id !== "art").map(({ id, label, icon: Icon }) => (
              <BottomBarButton
                key={id}
                label={label}
                active={sheet === "navigator" && section === id}
                badge={id === "inbox" ? unplaced.length : undefined}
                onClick={() => showSection(id)}
              >
                <Icon className="size-5" />
              </BottomBarButton>
            ))}
            <BottomBarButton
              label="Board"
              active={!sheet && activeTab?.type === "board"}
              onClick={() => {
                setSheet(null);
                openBoardTab(storyId);
              }}
            >
              <LayoutGrid className="size-5" />
            </BottomBarButton>
            <BottomBarButton label="Details" active={sheet === "inspector"} onClick={toggleInspector}>
              <PanelRightOpen className="size-5" />
            </BottomBarButton>
            {/* The sections that don't fit, by name */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <BottomBarButton
                  label="More"
                  active={sheet === "navigator" && (section === "chats" || section === "art")}
                  badge={artAttention}
                >
                  <Ellipsis className="size-5" />
                </BottomBarButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="w-44">
                {SECTIONS.filter((s) => s.id === "chats" || s.id === "art").map(({ id, label, icon: Icon }) => (
                  <DropdownMenuItem key={id} onSelect={() => showSection(id)}>
                    <Icon />
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>

        <Sheet open={sheet === "navigator"} onOpenChange={(open) => changeSheet(open ? "navigator" : null)}>
          <SheetContent
            side="left"
            showCloseButton={false}
            aria-describedby={undefined}
            className="w-[88vw] gap-0 p-0 sm:max-w-sm"
          >
            <SheetTitle className="sr-only">{SECTIONS.find((s) => s.id === section)?.label}</SheetTitle>
            <div className="flex items-center gap-1 border-b p-2">
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                value={section}
                onValueChange={(value) => value && showSection(value as Section)}
                aria-label="Section"
                className="flex-1"
              >
                {SECTIONS.map(({ id, label, icon: Icon }) => (
                  <ToggleGroupItem key={id} value={id} className="h-auto flex-1 flex-col gap-0.5 px-1 py-1 text-[10px]">
                    <Icon />
                    {label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              <SheetClose className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent">
                <X className="size-4" />
                <span className="sr-only">Close</span>
              </SheetClose>
            </div>
            <div data-region="panel" data-panel="left" className="min-h-0 flex-1 overflow-hidden bg-panel">
              {sectionPanel}
            </div>
          </SheetContent>
        </Sheet>

        <Sheet open={sheet === "inspector"} onOpenChange={(open) => changeSheet(open ? "inspector" : null)}>
          <SheetContent
            side="bottom"
            showCloseButton={false}
            aria-describedby={undefined}
            className="h-[85dvh] gap-0 overflow-hidden rounded-t-2xl p-0 pb-[env(safe-area-inset-bottom)]"
          >
            <SheetTitle className="sr-only">Details</SheetTitle>
            <div data-region="panel" data-panel="right" className="min-h-0 flex-1 overflow-hidden bg-panel">
              <InspectorPanel
                storyId={storyId}
                onCollapse={() => changeSheet(null)}
                onShowInbox={showInbox}
              />
            </div>
          </SheetContent>
        </Sheet>

        {dialogs}
      </FragmentDndProvider>
    );
  }

  return (
    <FragmentDndProvider storyId={storyId}>
      <div data-region="workspace" className="flex min-h-0 flex-1">
        <nav
          data-region="rail"
          aria-label="Story sections"
          className="flex w-16 shrink-0 flex-col items-center gap-1 overflow-y-auto border-r bg-panel py-2"
        >
          {SECTIONS.map(({ id, label, icon: Icon }) => (
            <RailButton
              key={id}
              label={label}
              active={navigatorOpen && section === id}
              onClick={() => showSection(id)}
              badge={id === "inbox" ? unplaced.length : id === "art" ? artAttention : undefined}
            >
              <Icon className="size-[18px]" />
            </RailButton>
          ))}
          <div className="my-1 h-px w-8 bg-border" />
          <RailButton label="Story board" name="Board" onClick={() => openBoardTab(storyId)} active={activeTab?.type === "board"}>
            <LayoutGrid className="size-[18px]" />
          </RailButton>
          <div className="mt-auto flex flex-col items-center gap-1">
            <RailButton
              label={navigatorOpen ? "Hide the sidebar" : "Show the sidebar"}
              name="Sidebar"
              shortcut={`${MOD}\\`}
              onClick={toggleNavigator}
            >
              {navigatorOpen ? <PanelLeftClose className="size-[18px]" /> : <PanelLeftOpen className="size-[18px]" />}
            </RailButton>
            <RailButton
              label={inspectorOpen ? "Hide the details" : "Show the details"}
              name="Details"
              shortcut={`${MOD}Shift+\\`}
              onClick={toggleInspector}
            >
              {inspectorOpen ? <PanelRightClose className="size-[18px]" /> : <PanelRightOpen className="size-[18px]" />}
            </RailButton>
            <div className="my-1 h-px w-8 bg-border" />
            <RailButton label="Jot down an idea" name="New idea" shortcut="Alt+N" onClick={openCapture}>
              <Plus className="size-[18px]" />
            </RailButton>
          </div>
        </nav>

        <ResizablePanelGroup
          orientation="horizontal"
          defaultLayout={layout.defaultLayout}
          onLayoutChanged={layout.onLayoutChanged}
          className="min-w-0 flex-1"
        >
          <ResizablePanel
            id="navigator"
            panelRef={navigatorRef}
            defaultSize="280px"
            minSize="220px"
            maxSize="480px"
            collapsible
            groupResizeBehavior="preserve-pixel-size"
            onResize={(size) => setNavigatorOpen(size.inPixels > 0)}
          >
            <div data-region="panel" data-panel="left" className="h-full overflow-hidden bg-panel">
              {sectionPanel}
            </div>
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel id="main" minSize="360px">
            <div data-region="panel" data-panel="center" className="h-full min-w-0 overflow-hidden">
              <TabbedContent storyId={storyId} onShowInbox={showInbox} onCapture={openCapture} />
            </div>
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel
            id="inspector"
            panelRef={inspectorRef}
            defaultSize="320px"
            minSize="260px"
            maxSize="560px"
            collapsible
            groupResizeBehavior="preserve-pixel-size"
            onResize={(size) => setInspectorOpen(size.inPixels > 0)}
          >
            <div data-region="panel" data-panel="right" className="h-full overflow-hidden bg-panel">
              <InspectorPanel
                storyId={storyId}
                onCollapse={() => (onBoard && detailsPinned ? setBoardDetailsPinned(false) : inspectorRef.current?.collapse())}
                onShowInbox={showInbox}
                headerActions={
                  onBoard && detailsPinned ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      onClick={() => setBoardDetailsPinned(false)}
                      aria-label="Show the details over the board instead"
                      title="Show the details over the board instead"
                    >
                      <PinOff className="size-4" />
                    </Button>
                  ) : undefined
                }
              />
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {dialogs}
    </FragmentDndProvider>
  );
}

function BottomBarButton({
  label,
  active,
  badge,
  children,
  ...props
}: {
  label: string;
  active?: boolean;
  badge?: number;
  children: ReactNode;
} & ComponentProps<"button">) {
  return (
    <button
      type="button"
      {...props}
      aria-pressed={active}
      className={cn(
        "flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] text-muted-foreground transition-colors active:bg-accent",
        active && "text-primary"
      )}
    >
      <span className="relative">
        {children}
        {badge ? (
          <span className="absolute -right-2.5 -top-1.5 min-w-4 rounded-full bg-primary px-1 text-center text-[10px] font-semibold leading-4 text-primary-foreground">
            {badge > 99 ? "99+" : badge}
          </span>
        ) : null}
      </span>
      {label}
    </button>
  );
}
